<script setup lang="ts">
import FlagsLanguage from "../FlagsLanguage.vue";
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useAuthStore, useUsersStore } from "@/stores";
import router from '@/router';
import { useDisplay } from 'vuetify/lib/framework.mjs';
import { useRoute, type RouteLocationRaw } from 'vue-router';
import { useI18n } from "vue-i18n";
import { LangType } from "@/helpers/types";

const { t } = useI18n();

const authStore = useAuthStore();
const route = useRoute();
const isAuthenticated = computed(() => authStore.isAuth);
const isStoreOwner = computed(() => authStore.isStoreOwner);
const isAdmin = computed(() => authStore.isAdmin);
const showMobileMenu = ref(false);

const user = computed(() => authStore.user);
const currentSubscription = computed(() => user.value?.currentSubscription);
const currentPlan = computed(() => currentSubscription?.value?.plan);

const apiUrl = import.meta.env.VITE_APP_API_URL;

const toggleDrawer = () => {
  showMobileMenu.value = !showMobileMenu.value;
  console.log("Ativando menu");
};

const navigateTo = (route: RouteLocationRaw) => {
  showMobileMenu.value = false;
  router.push(route);
};

const { smAndDown, md, lg, xlAndUp, xxl } = useDisplay();

const isSmall = computed(() => smAndDown.value);

const isMobile = computed(() => isSmall.value);

onMounted(() => {
  const closeMenuOnClickOutside = (event: MouseEvent) => {
    if (showMobileMenu.value && !(event.target as Element).closest('.v-navigation-drawer') && !(event.target as Element).closest('.menu-btn')) {
      showMobileMenu.value = false;
    }
  };
  document.addEventListener('click', closeMenuOnClickOutside);

  const smoothScroll: EventListener = (event) => {
    const anchor = (event.target as Element).closest('a');
    const href = anchor?.getAttribute('href') ?? '';
    const isInternalAnchor = href.startsWith('#') && href.length > 1;
    if (!isInternalAnchor) return;
    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      event.preventDefault();
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const anchorLinks = document.querySelectorAll('nav a[href^="#"]');
  anchorLinks.forEach(link => {
    link.addEventListener('click', smoothScroll);
  });

  onUnmounted(() => {
    document.removeEventListener('click', closeMenuOnClickOutside);
    anchorLinks.forEach(link => {
      link.removeEventListener('click', smoothScroll);
    });
  });
});

const dialogTransition = computed(() => showMobileMenu.value ? 'slide-in' : 'slide-out');

function logout() {
  authStore.logout();
}

const userImageUrl = computed(() => {
  if (authStore.user && authStore.user.image) {
    return authStore.user.image;
  }
  return '/img/icons/person-black.png';
});

const canShowRegularMenu = computed(() => {
  return route?.name && !(route?.name as string)?.startsWith('formdata_source_')
});

const loginUrl = import.meta.env.VITE_APP_LOGIN_URL;

watch(
  () => route.name,
  (_) => {
    showMobileMenu.value = false;
  }
);
</script>

<template>
  <div :class="['active-mobile', dialogTransition]"></div>
  <v-app-bar v-show="canShowRegularMenu" style="box-shadow: none; position: relative" class="custom__app__bar" app
    color="transparent" :transition="dialogTransition" :class="dialogTransition" dark>
    <v-container class="custom-container header">
      <nav class="nav-desktop">
        <v-btn icon class="menu-btn" v-if="isMobile" @click="toggleDrawer">
          <v-icon color="#000">mdi-menu</v-icon>
        </v-btn>
        <v-row class="ma-0 menu-main">
          <v-col class="pa-0" cols="5" sm="5" md="2" lg="2" xl="2">
            <router-link :to="{ name: 'home' }">
              <div class="logo__container" :style="{
                backgroundImage: `url(/img/icons/logoblue.png)`,
              }"></div>
            </router-link>
          </v-col>
          <div class="menu-router" v-if="!isMobile">
            <router-link class="mx-1" active-class="active" v-if="!isMobile" to="/">{{
              isAuthenticated ? "Dashboard" : t("headerComponent.home")
              }}</router-link>
            <router-link class="mx-1" active-class="active" v-if="isAdmin" :to="{ name: 'priceTables' }">{{
              t("headerComponent.adm.priceList") }}</router-link>
            <router-link class="mx-1" active-class="active" v-if="isAdmin" :to="{ name: 'userList' }">{{
              t("headerComponent.adm.users") }}</router-link>
            <router-link class="mx-1" active-class="active" v-if="!isAuthenticated" :to="{ path: '/sobre-nos' }">{{
              t("headerComponent.aboutUs") }}</router-link>
            <router-link class="mx-1" active-class="active" v-if="!isAuthenticated" :to="{ path: '/planos' }">{{
              t("headerComponent.plans") }}</router-link>
            <router-link class="mx-1" active-class="active" v-if="!isAuthenticated" :to="{ path: '/contato' }">{{
              t("headerComponent.contact") }}</router-link>
          </div>
          <template v-if="isAuthenticated">
            <div class="header-admin" v-if="!isMobile">
              <router-link class="mx-6 router r-reservas" to="/u/reserves" v-if="isStoreOwner && !isMobile">{{
                t("headerComponent.isntMobile.isStoreOwner.reservations") }}</router-link>
              <router-link class="mx-6 router r-reservas" :to="{
                name: 'Houses',
              }" v-if="isStoreOwner && !isMobile">{{ t("headerComponent.isntMobile.isStoreOwner.property")
                }}</router-link>
              <router-link class="mx-6 router r-subscription" to="/u/subscription" v-if="isStoreOwner && !isMobile">
                <small>
                  <small>{{ t("headerComponent.isntMobile.isStoreOwner.subscription") }}:</small>
                </small>
                <br />
                <small>
                  {{ currentPlan?.name || "-" }}
                </small>
              </router-link>

              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn class="btn-user" v-bind="props" :ripple="false">
                    <template #prepend>
                      <v-avatar>
                        <img :style="{
                          width: '100%',
                          height: '100%',
                        }" :src="userImageUrl" alt="Avatar" class="img-user" />
                      </v-avatar>
                    </template>
                    <v-icon>mdi-chevron-down</v-icon>
                  </v-btn>
                </template>
                <v-list rounded="lg">
                  <v-list-item>
                    <v-btn block variant="outlined" rounded="lg" density="comfortable" prepend-icon="mdi-cog-outline"
                      color="#151F45" class="px-12" to="/u/config">{{
                        t("headerComponent.isntMobile.isAuthenticated.settings") }}</v-btn>
                    <v-btn block variant="outlined" rounded="lg" density="comfortable" prepend-icon="mdi-play-outline"
                      color="#151F45" class="px-12 my-2" to="/u/videos">{{
                        t("headerComponent.isntMobile.isAuthenticated.explanatoryVideos") }}</v-btn>
                    <v-btn block variant="outlined" rounded="lg" density="comfortable" prepend-icon="mdi-logout"
                      color="red" class="px-12" @click="logout">{{
                        t("headerComponent.isntMobile.isAuthenticated.logOut") }}</v-btn>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
          <template v-else>
            <div class="auth" v-if="!isMobile">
              <a class="login mx-1" :href="loginUrl">{{ t("headerComponent.login") }}</a>
              <v-btn class="btn-entrar" to="/register">{{ t("headerComponent.register") }}</v-btn>

              <FlagsLanguage :supported-languages="[LangType.pt_BR, LangType.en_US, LangType.es_ES]" />
            </div>
          </template>
        </v-row>
      </nav>
      <!-- --------MOBILE------ -->
      <v-navigation-drawer v-model="showMobileMenu" permanent left fixed>
        <v-list>
          <div class="mobile-nav-items">
            <div class="w-100 mb-5">
              <router-link to="/">
                <v-responsive :aspect-ratio="1" width="100%">
                  <div :style="{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(/img/icons/logoblue.png)`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                  }"></div>
                </v-responsive>
              </router-link>
            </div>
            <v-divider width="100%"></v-divider>
            <v-list-item @click="navigateTo('/')">
              <v-list-item-title>{{
                isAuthenticated ? "Dashboard" : t("headerComponent.home")
                }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="navigateTo({ name: 'priceTables' })" v-if="isAdmin">
              <v-list-item-title>{{ t("headerComponent.adm.priceList") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="navigateTo({ name: 'userList' })" v-if="isAdmin">
              <v-list-item-title>{{ t("headerComponent.adm.users") }}</v-list-item-title>
            </v-list-item>
            <v-divider width="100%"></v-divider>
            <v-list-item v-if="!isAuthenticated" @click="navigateTo('/sobre-nos')">
              <v-list-item-title>{{ t("headerComponent.aboutUs") }}</v-list-item-title>
            </v-list-item>
            <v-divider width="100%"></v-divider>
            <v-list-item v-if="!isAuthenticated" @click="navigateTo('/planos')">
              <v-list-item-title>{{ t("headerComponent.plans") }}</v-list-item-title>
            </v-list-item>
            <v-divider width="100%"></v-divider>
            <v-list-item v-if="!isAuthenticated" @click="navigateTo('/contato')">
              <v-list-item-title>{{ t("headerComponent.contact") }}</v-list-item-title>
            </v-list-item>

            <v-list-item :to="'/u/reserves'">
              <v-list-item-title>{{ t("headerComponent.isntMobile.isStoreOwner.reservations") }}</v-list-item-title>
            </v-list-item>

            <v-list-item>
              <FlagsLanguage  :supported-languages="[LangType.pt_BR, LangType.en_US, LangType.es_ES]" />
            </v-list-item>
            <v-list-item v-if="isStoreOwner" :to="{
              name: 'Houses',
            }">
              <v-list-item-title>{{ t("headerComponent.isntMobile.isStoreOwner.property") }}</v-list-item-title>
            </v-list-item>

            <v-list-item :to="'/u/subscription'" v-if="isStoreOwner">
              <v-list-item-title>
                <<small>{{ t("headerComponent.isntMobile.isStoreOwner.subscription") }}:</small> <br />
                  <span>{{ currentPlan?.name || "-" }}</span>
              </v-list-item-title>
            </v-list-item>
          </div>
          <div class="auth auth__mobile">
            <a v-if="!isAuthenticated" class="login mx-1" :href="loginUrl">{{ t("headerComponent.login") }}</a>
            <v-btn class="btn-entrar btn__entrar__mobile" v-if="!isAuthenticated" to="/register">{{
              t("headerComponent.register") }}</v-btn>
            <v-btn v-if="isAuthenticated" class="btn__entrar__mobile" color="red" @click="logout">{{
              t("headerComponent.isntMobile.isAuthenticated.logOut") }}</v-btn>
          </div>
        </v-list>
      </v-navigation-drawer>
    </v-container>
  </v-app-bar>
</template>
<style scoped>
.custom__app__bar :deep(.v-toolbar__content) {
  height: max-content !important;
}

.custom-container.header {
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 2rem;
}

.card-login {
  border-radius: 2rem !important;
}

.header nav {
  display: flex;
  align-items: center;
  gap: 2rem;
  padding-right: 1rem;
}

.header nav .menu-router a {
  text-decoration: none;
  color: #8c8c92;
  font-size: 1.3rem;
  font-weight: 400;
}

.header nav .auth {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header nav .auth .login {
  text-decoration: none;
  color: #8c8c92;
  font-size: 1.3rem;
  font-weight: 400;
}

.auth__mobile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.header nav .menu-router a.active {
  color: #35aefa;
}

.header .menu-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header .menu-main .menu-router {
  display: flex;
  gap: 1.5rem;
}

.btn-entrar {
  background: #209deb;
  color: #fff;
  text-transform: capitalize;
  font-size: 1.2rem;
  font-weight: 400;
}

.header-admin {
  display: flex;
  align-items: center;
  padding-left: 1rem;
}

.header-admin .router {
  text-decoration: none;
  color: #151f45;
}

.header-admin .r-reservas {
  font-size: 1.3rem;
}

.header-admin .r-subscription {
  font-size: 1.3rem;
  line-height: 0.9;
  display: inline-block;
}

.header-admin .credit {
  line-height: 1.1;
}

.header-admin .credit strong {
  font-size: 1.1rem;
}

.header .img-user {
  border-radius: 50%;
  border: 3px solid #000;
}

.header .btn-user .mdi-chevron-down {
  font-size: 1.5rem;
}

.header .btn-user {
  height: 4rem;
  padding: 0 10px;
}

.logo__container {
  width: 100%;
  height: 4rem;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}

/* MOBILE */
.active-mobile {
  display: none;
}

.header nav .img-header {
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
}

.header nav .img-self {
  width: 10vw;
}

.nav-desktop {
  width: 100%;
}

@media (max-width: 768px) {
  .active-mobile.slide-in {
    height: 10rem;
    display: inline;
  }

  .header nav {
    top: 0 !important;
  }

  .v-app-bar {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .menu-btn {
    order: 1;
  }

  .app-py {
    padding-block: 20px;
  }

  .v-toolbar.slide-in {
    height: 100%;
    position: fixed !important;
  }

  .v-toolbar.slide-in .v-navigation-drawer {
    animation-name: slideIn;
    animation-duration: 0.3s;
  }

  .v-toolbar.slide-out {
    animation-name: slideOut;
    animation-duration: 0.3s;
  }

  .v-navigation-drawer .v-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: space-between;
  }

  .v-navigation-drawer {
    height: 100% !important;
    z-index: 100;
  }

  .v-navigation-drawer__scrim {
    position: relative !important;
  }

  /* Overlay Styles */
  .overlay {
    position: fixed;
    top: 0;
    padding-top: 5rem;
    right: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    z-index: 9;
  }

  .v-navigation-drawer {
    width: 250px;
  }

  .header .btn-entrar:not(.btn__entrar__mobile) {
    display: none;
  }

  .header .img-header-mobile {
    display: flex;
    width: 50vw;
    flex-wrap: nowrap;
    gap: 1rem;
  }

  .header .img-header-mobile .img-self {
    width: 50%;
  }

  .v-app-bar {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .header .btn-entrar-mobile {
    background: #151f45;
    color: #fff;
    text-transform: capitalize;
    font-size: 1rem;
    font-weight: 400;
    width: 100%;
  }
}

@keyframes slideIn {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
}

.header .img-user img {
  object-fit: cover !important;
}
</style>
